import React from "react";
import AuthFormCard from "components/auth-form-card/AuthFormCard";
import Button from "components/button/Button";
import { useAuth } from "auth";
import "./session-changed.css";

/**
 * Shown when this tab holds a Firebase user its stored identity does not belong
 * to — i.e. a different account signed in elsewhere in this browser.
 *
 * It offers no login form, because a login form is how a second principal took
 * over the shared session in the first place, and no route to the dashboard,
 * because the tab would then act as someone it is not displaying.
 *
 * TWO exits, because a mismatch has three causes and reload only resolves one:
 *
 *   Reload    authUid is stale in memory but correct in localStorage — another
 *             tab signed in after this one booted. Rehydrating binds the tab to
 *             whoever the browser is actually signed in as.
 *   Sign out  localStorage holds no authUid (a session predating uid-binding) or
 *             one for a different uid. Reload re-reads those same two stores and
 *             lands right back here, forever — the CRYP-4925 dead end. Signing
 *             out clears the Firebase user, so identityState reports
 *             "signed-out" and the login form becomes reachable again.
 *
 * Sign-out must be the real signOut(). A dispatch that merely cleared authUid
 * would stamp this tab's view over the ORIGIN-SHARED persist:user blob —
 * redux-persist writes the whole slice on any action — and drop a legitimately
 * signed-in tab into this same dead end on its next reload.
 *
 * It is also deliberately a click, not automatic: in the Reload case the
 * mismatch is benign, and auto-signing-out would let a backgrounded tab destroy
 * a sign-in that another tab had just completed.
 */
function SessionChangedScreen() {
  const { signOut, signingOut } = useAuth();

  return (
    <AuthFormCard
      title="Your session changed"
      subtitle="A different account signed in to this browser in another tab, so this tab is no longer showing your account."
    >
      <p className="v1-auth-nav">
        Reload to continue as the account that is currently signed in — your
        other tab is unaffected.
      </p>
      {/* Highlighted, not folded into the paragraph above: for the two mismatch
          causes reload cannot fix, this sentence is the only exit. A user who
          skims past it is left pressing Reload against a screen that will never
          change. */}
      <p className="gp-session-changed__hint">
        If reloading brings you back here, sign out and log in again.
      </p>
      <div className="gp-session-changed__actions">
        <Button
          variant="primary"
          size="md"
          disabled={signingOut}
          onClick={() => window.location.reload()}
        >
          Reload
        </Button>
        <Button
          variant="ghost"
          size="md"
          disabled={signingOut}
          onClick={signOut}
        >
          {signingOut ? "Signing out…" : "Sign out"}
        </Button>
      </div>
    </AuthFormCard>
  );
}

export default SessionChangedScreen;
